/**
 * @param date in  { day , month , year } format
 * @returns date in YYYY-MM-DD format
 */
export function joinDateObj(date: any) {
  let values = Object.values(date);
  values.reverse();
  return values.join('-');
}

/** A date as the customer-profile picker edits it — day, month (1-12) and year, each optional. */
export interface DateParts {
  d?: string;
  m?: string;
  y?: string;
}

// Unpadded, so "05" matches the picker's "5" option. Anything after the digits (an ISO time) is dropped.
const toPart = (segment?: string) => {
  const n = parseInt(segment ?? '', 10);
  return Number.isNaN(n) ? undefined : String(n);
};

/**
 * The saved "y-m-d" string -> picker parts. The saved string stays the only source of truth: a
 * half-picked date is held as e.g. "--5", and splits back to `{ d: "5" }`.
 */
export function splitSavedDate(value?: string | null): DateParts {
  if (!value) {
    return {};
  }
  const [y, m, d] = String(value).split('-');
  return { d: toPart(d), m: toPart(m), y: toPart(y) };
}

/**
 * Picker parts -> the "y-m-d" string the form holds (see `splitSavedDate`). A day past the month's
 * end (31 Feb) is clamped to its last day — a saved date locks, so a bad pick could not be undone.
 * With no year yet, Feb allows 29; picking a non-leap year afterwards clamps it again.
 */
export function joinSavedDate({ d, m, y }: DateParts): string {
  const lastDay = m ? new Date(Number(y) || 2000, Number(m), 0).getDate() : 31;
  const day = d && Number(d) > lastDay ? String(lastDay) : d;
  return `${y ?? ''}-${m ?? ''}-${day ?? ''}`;
}

/**
 * What a customer payload sends for a picker-driven date. A half-picked "y-m-d" (an empty segment,
 * only `joinSavedDate` makes one) is not a date, so it goes as null; anything else, including a
 * value loaded from the API and never touched, goes out exactly as it came in.
 */
export function savedDateOrNull(value?: string | null): string | null | undefined {
  const segments = typeof value === 'string' ? value.split('-') : [];

  if (segments.length === 3 && segments.includes('')) {
    return null;
  }

  if (segments.length === 3 && segments.every(Boolean)) {
    const [y, m, d] = segments;
    return `${y}-${m.padStart(2, '0')}-${d.padStart(2, '0')}`;
  }

  return value;
}

/**
 * Time-of-day greeting, e.g. the kiosk header's "Good evening".
 *
 * Lives here rather than in the header component because `packages/ui/kiosk/components`
 * are pure leaves with no test harness — this is the half worth asserting.
 */
export function greetingForDate(date: Date = new Date()): string {
  const hour = date.getHours();
  if (hour < 12) {
    return 'Good morning';
  }
  if (hour < 17) {
    return 'Good afternoon';
  }
  return 'Good evening';
}
